import axios, {AxiosError, AxiosInstance} from 'axios'
import _ from 'lodash'
import {generateRecaptchaToken} from '../common/recapctcha'
import {
  APDataRoot,
  APProjectDetailsRoot,
  SelectedParcelProps,
} from '../components/aerialphotography-components/ap.slice'
import {AP_SEARCH_PATH, OPEN_LAYERS_AIR_PHOTO_PATH, OPEN_LAYERS_BASE_URL, RECAPTCHA_ENABLED,} from '../environment'
import RecaptchaRequiredError from '../errors/RecaptchaRequiredError'
import {GeoJSON, WKT} from "ol/format"

const AXIOS_CONFIG = {
  headers: { 'X-RDS-AJAX': 'true', 'Accept-Language': 'en-AU' },
  baseURL: AP_SEARCH_PATH,
  withCredentials: true,
}

class AerialPhotographyServiceAPI {

  client: AxiosInstance
  airphotoClient: AxiosInstance

  constructor() {
    this.client = axios.create(AXIOS_CONFIG)
    this.airphotoClient = axios.create()
  }

  getTransformedGeometryForFeatureRequest (payload: SelectedParcelProps) {
    const geoJSON = new GeoJSON();
    const wkt = new WKT();

    // FIXME Future benevolent developer, please untangle this unholy mess of projection conversions
    const geometry = geoJSON.readGeometry(payload.geometry,{ dataProjection: payload.projection ?? 'EPSG:3857', featureProjection: payload.projection ?? 'EPSG:4283'})
    return wkt.writeGeometry(geometry, {
      dataProjection: 'EPSG:4326',
      featureProjection: payload.projection ?? 'EPSG:3857'
    })
  }

  fetchProjectDetails = async (payload: SelectedParcelProps) => {
    try {
      const filmTypeNoRegex = /^([^-]*)-([0-9]+)$/
      let cqlFilter = '';

      if (payload.geometry) {
        cqlFilter = `INTERSECTS(GEOMETRY, ${this.getTransformedGeometryForFeatureRequest(payload)})`
      }

      if (payload.filmNo && payload.frameNo) {
        let filmType = undefined
        let filmNo = payload.filmNo
        let match = payload.filmNo.match(filmTypeNoRegex)

        if (match && match.length === 3) {
          filmType = match[1]
          filmNo = match[2]

          if (filmType !== '') {
            cqlFilter = `FILM_TYPE='${filmType}' AND`
          }
        }

        cqlFilter = `${cqlFilter} FILM=${filmNo} AND FRAME=${payload.frameNo}`
      }

      const featureRequest = {
        service: 'WFS',
        version: '1.0.0',
        request: 'GetFeature',
        srsName: 'EPSG:4283',
        typeName: 'airphoto:AIRPHOTO_FRAME_COVERAGE',
        outputFormat: 'application/json',
        cql_filter: cqlFilter
      }

      // @ts-ignore
      let body = new URLSearchParams(featureRequest);

      let projectData = await this.airphotoClient.post(OPEN_LAYERS_AIR_PHOTO_PATH,
        body,
        {
          baseURL: OPEN_LAYERS_BASE_URL,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
      })

      // We need to set whether or not the polygon has been drawn by the user so we can determine
      // which frames to show later on. For the drawn polygons we want to default to showing all the frames, the
      // reasoning being that this will force the view to the maximum extent of the polygon automatically.
      // If the user has selected a parcel / placename, then this will be ignored and the existing behaviour will be
      // maintained. The corresponding frame filtering is performed in the `ap.slice.ts` module.
      let details: APProjectDetailsRoot = {
        laiskey: payload.LAISKEY || '',
        drawnPolygon: payload.drawn,
        frameDetails: []
      }

      projectData.data.features.forEach( (feature:any ) =>{
        details.frameDetails.push ( {
          id: feature.properties.ID,
          project: feature.properties.PROJECT,
          run: feature.properties.RUN,
          scale: feature.properties.SCALE,
          year: feature.properties.YEAR,
          contractor: "not defined",
          frame: feature.properties.FRAME,
          frameIdentifier: feature.properties.FRAME_IDENTIFIER,
          type: feature.properties.TYPE,
          film: feature.properties.FILM,
          film_type: feature.properties.FILM_TYPE,
          date: feature.properties.PHOTO_DATE_TEXT,
          camera: feature.properties.CAMERA,
          geometry: feature.geometry
        } )
      })

      // Sort by year (desc), project name (desc) then frame number (desc)
      details.frameDetails = _.orderBy(details.frameDetails, ['year', 'project', 'frame'], ['desc', 'desc', 'desc'])

      return details;

    } catch (error: any) {
      return this.handleAPDataError(error)
    }
  }

  fetchAPData = async (payload: SelectedParcelProps) => {
    // require recaptcha
    if (RECAPTCHA_ENABLED) {
      const isV3Captcha = !payload.recaptchaV2Code
      const recaptchaToken = await generateRecaptchaToken()
      return await this.fetchAPDataWithRecaptcha(recaptchaToken, payload, isV3Captcha)
    } else {
      return await this.fetchAPDataWithoutRecaptcha(payload)
    }
  }

  /**
   * Fetch Aerial Photography data asynchronously. update apData in the redux store with the results.
   * With or without a recaptcha
   * @returns {Promise<void>}
   */

  fetchAPDataWithoutRecaptcha = async (result: SelectedParcelProps) => {
    try {
      let apData = await this.client.get<APDataRoot>('/data', {
        params: {
          laisKey: result.LAISKEY,
        },
      })
      return apData.data
    } catch (error: any) {
      return this.handleAPDataError(error)
    }
  }

  fetchAPDataWithRecaptcha = async (captchaCode: string, result: SelectedParcelProps, isV3recaptcha: boolean) => {
    try {
      let apData = await this.client.get<APDataRoot>('/dataWithRecaptcha', {
        params: {
          laisKey: result.LAISKEY,
          [isV3recaptcha ? 'recaptchaV3Key' : 'recaptchaV2Key']: captchaCode,
        },
      })

      return apData.data
    } catch (error: any) {
      return this.handleAPDataError(error)
    }
  }

  /* *********************************************************************** */

  handleAPDataError = (error: any) => {
    if (_.isEmpty(error.response) || _.isEmpty(error.response?.data) || _.isEmpty(error.response?.data.type)) {
      // Unable to contact the server.
      return {
        error: true,
        errorType: 'INTERNAL_ERROR',
        errorMessage: 'Internal Server Error',
      }
    } else if (error.response?.data.type === 'INVALID_CAPTCHA') {
      // V3 recaptcha failed (low score) force V2 recaptcha
      throw new RecaptchaRequiredError('error.response.data.type', 'Recaptcha failed (V3), manual Recaptcha required! (V2)')
    } else if (error.response?.data.type === 'INVALID_PARCEL') {
      // No parcel found / parcel is not available online, update valuation data with an error.
      return {
        error: true,
        errorType: error.response.data.type,
        errorMessage: error.response.data.errorMessage,
      }
    } else if (error.message !== 'CANCEL') {
      return {
        error: true,
        errorType: 'INTERNAL_ERROR',
        errorMessage: 'Internal Server Error',
      }
    }
  }
}

const aerialPhotographyServiceAPI = new AerialPhotographyServiceAPI()

export default aerialPhotographyServiceAPI